import CollectionHeroBanner from "@components/Section/CollectionHeroBanner/CollectionHeroBanner";
import Container from "@components/Section/Container/Container";
import { type ComponentProps } from "@ts/components";
import { type Documents } from "@ts/sanity";
import React, { memo, useMemo, type FC } from "react";
import Accordion from "../Section/Accordion/Accordion";
import Banner from "../Section/Banner/Banner";
import CollectionButtonSlider from "../Section/CollectionButtonSlider/CollectionButtonSlider";
import { FeaturedArticle } from "../Section/FeaturedArticle/FeaturedArticle";
import { FeaturedCollection } from "../Section/FeaturedCollection/FeaturedCollection";
import FeaturedCollectionTiles from "../Section/FeaturedCollectionTiles/FeaturedCollectionTiles";
import FeaturedImageContent from "../Section/FeaturedImageContent/FeaturedImageContent";
import Hero from "../Section/Hero/Hero";
import HeroDuo from "../Section/HeroDuo/HeroDuo";
import HeroProductFeature from "@components/Section/HeroProductFeature/HeroProductFeature";
import Image from "../Section/Image/Image";
import ImageCarousel from "../Section/ImageCarousel/ImageCarousel";
import ImageGrid from "../Section/ImageGrid/ImageGrid";
import Instagram from "../Section/Instagram/Instagram";
import ResponsiveSwiper from "../Section/ResponsiveSwiper/ResponsiveSwiper";
import Richtext from "../Section/Richtext/Richtext";
import SellingPoints from "../Section/SellingPoints/SellingPoints";
import SizeGuide from "../Section/SizeGuide/SizeGuide";
import TestimonialSlider from "../Section/TestimonialSlider/TestimonialSlider";
import TextGrid from "../Section/TextGrid/TextGrid";
import Title from "../Section/Title/Title";
import UniqueSellingPoint from "../Section/UniqueSellingPoint/UniqueSellingPoint";
import Video from "../Section/Video/Video";
import Ticker from "@components/Section/Ticker/Ticker";

import { FeaturedProductCarousel } from "~/components/Section/FeaturedProductCarousel";

import {
  SectionTypeform as Typeform,
  SectionKlaviyoEmbeddedForm as KlaviyoEmbeddedForm,
} from "@bared/ui";

export type SectionsProps = Extract<Documents, { anchorId?: string }>;

type DocumentWithSections = Extract<Documents, { content?: SectionsProps }>;

type SectionsInputProps = ComponentProps & {
  nested?: boolean;
  page: Partial<DocumentWithSections>;
};

type SectionsComponentProps = ComponentProps &
  SectionsProps & {
    nested?: boolean;
    component: React.ComponentType<SectionsProps>;
    key: string;
  };

export type SectionsOutputProps = ComponentProps & {
  content: SectionsComponentProps[];
};

export const ComponentMap = {
  Accordion,
  Banner,
  CollectionButtonSlider,
  CollectionHeroBanner,
  Container,
  FeaturedArticle,
  FeaturedCollection,
  FeaturedCollectionTiles,
  FeaturedImageContent,
  FeaturedProductCarousel,
  Hero,
  HeroDuo,
  HeroProductFeature,
  Image,
  ImageCarousel,
  ImageGrid,
  Instagram,
  ResponsiveSwiper,
  Richtext,
  SellingPoints,
  SizeGuide,
  TestimonialSlider,
  TextGrid,
  Ticker,
  Title,
  UniqueSellingPoint,
  Video,
  Typeform,
  KlaviyoEmbeddedForm,
};

type ComponentMapKey = keyof typeof ComponentMap;

export const withSections = (Component: FC<SectionsOutputProps>) =>
  memo(({ name = "Sections", nested, page, ...props }: SectionsInputProps) => {
    const sections = page?.content?.map((section) => {
      const key = section?._key ?? section?._id;
      const fileName = section?._type?.replace(
        "section",
        "",
      ) as unknown as ComponentMapKey;
      const SectionComponent = ComponentMap[fileName];

      if (!SectionComponent) {
        return null;
      }

      return {
        ...section,
        ...props,
        nested,
        component: SectionComponent,
        key: `${fileName}_${key}`,
      };
    });

    Component.displayName = name;

    return useMemo(() => <Component {...props} content={sections} />, [page]);
  });
